import { useEffect } from 'react';
import { useTranslation } from './useTranslation';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
}

export const useSEO = (props: SEOProps = {}) => {
  const { language, t } = useTranslation();

  const defaultTitles = {
    FR: 'DiskOver - Explorez le patrimoine mondial à travers des podcasts culturels de 3 minutes🔍',
    EN: 'DiskOver - Explore world heritage through 3-minute cultural podcasts🔍'
  };

  const defaultDescriptions = {
    FR: 'Découvrez et créez des podcasts culturels courts et accrocheurs. Explorez des lieux fascinants à travers des histoires audio immersives de 3 minutes. Voyage, culture et découverte à portée d\'oreilles.',
    EN: 'Discover and create short and captivating cultural podcasts. Explore fascinating places through immersive 3-minute audio stories. Travel, culture and discovery at your fingertips.'
  };

  const defaultKeywords = {
    FR: 'podcast, voyage, culture, histoire, audio, exploration, découverte, souvenir, lieux, patrimoine, tourisme culturel',
    EN: 'podcast, travel, culture, history, audio, exploration, discovery, memory, places, heritage, cultural tourism'
  };

  useEffect(() => {
    // Update document title
    const title = props.title || defaultTitles[language];
    document.title = title;

    // Update meta description
    const description = props.description || defaultDescriptions[language];
    const metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (metaDescription) {
      metaDescription.content = description;
    }

    // Update meta keywords
    const keywords = props.keywords || defaultKeywords[language];
    const metaKeywords = document.querySelector('meta[name="keywords"]') as HTMLMetaElement;
    if (metaKeywords) {
      metaKeywords.content = keywords;
    }

    // Update lang attribute
    document.documentElement.lang = language.toLowerCase();

    // Update Open Graph title
    const ogTitle = props.ogTitle || title;
    const metaOgTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
    if (metaOgTitle) {
      metaOgTitle.content = ogTitle;
    }

    // Update Open Graph description
    const ogDescription = props.ogDescription || description;
    const metaOgDescription = document.querySelector('meta[property="og:description"]') as HTMLMetaElement;
    if (metaOgDescription) {
      metaOgDescription.content = ogDescription;
    }

    // Update Open Graph locale
    const metaOgLocale = document.querySelector('meta[property="og:locale"]') as HTMLMetaElement;
    if (metaOgLocale) {
      metaOgLocale.content = language === 'FR' ? 'fr_FR' : 'en_US';
    }

    // Update Twitter title
    const metaTwitterTitle = document.querySelector('meta[name="twitter:title"]') as HTMLMetaElement;
    if (metaTwitterTitle) {
      metaTwitterTitle.content = ogTitle;
    }

    // Update Twitter description
    const metaTwitterDescription = document.querySelector('meta[name="twitter:description"]') as HTMLMetaElement;
    if (metaTwitterDescription) {
      metaTwitterDescription.content = ogDescription;
    }

    // Update canonical URL based on language
    const canonical = props.canonical || (language === 'FR' ? 'https://fr.diskover.ovh/' : 'https://diskover.ovh/');
    const linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (linkCanonical) {
      linkCanonical.href = canonical;
    }

    // Update structured data
    updateStructuredData(language, title, description);

  }, [language, props.title, props.description, props.keywords, props.ogTitle, props.ogDescription, props.canonical]);

  const updateStructuredData = (lang: 'FR' | 'EN', title: string, description: string) => {
    // Update Organization structured data
    const orgScript = document.querySelector('script[type="application/ld+json"]');
    if (orgScript) {
      const orgData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "DiskOver",
        "url": lang === 'FR' ? "https://fr.diskover.ovh" : "https://diskover.ovh",
        "logo": "https://diskover.ovh/logo.png",
        "description": lang === 'EN' 
          ? "Short cultural podcast platform to explore world heritage" 
          : "Plateforme de podcasts culturels courts pour explorer le patrimoine mondial",
        "foundingDate": "2024",
        "sameAs": []
      };
      orgScript.textContent = JSON.stringify(orgData);
    }

    // Update WebSite structured data
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    if (scripts[1]) {
      const websiteData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "DiskOver",
        "url": lang === 'FR' ? "https://fr.diskover.ovh" : "https://diskover.ovh",
        "description": description,
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${lang === 'FR' ? "https://fr.diskover.ovh" : "https://diskover.ovh"}/?search={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      };
      scripts[1].textContent = JSON.stringify(websiteData);
    }

    // Update WebApplication structured data
    if (scripts[2]) {
      const appData = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "DiskOver",
        "url": lang === 'FR' ? "https://fr.diskover.ovh" : "https://diskover.ovh",
        "applicationCategory": "AudioObject",
        "operatingSystem": "Web",
        "description": lang === 'EN' 
          ? "Web application to discover and create short and captivating cultural podcasts"
          : "Application web pour découvrir et créer des podcasts culturels courts et accrocheurs",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": lang === 'EN' ? "USD" : "EUR"
        }
      };
      scripts[2].textContent = JSON.stringify(appData);
    }
  };
};